import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence, useInView } from "motion/react";
import feature1 from "../assets/feature-1.mp4";
import feature2 from "../assets/feature-2-optimized.mp4";
import feature3 from "../assets/Feature-gif-project-1.mp4";
import { LiquidMetalButton } from "../components/ui/liquid-metal-button";
import { ArrowUpRight } from "lucide-react";
import { Backlight } from "../components/ui/backlight";
import { OrbitingCircles } from "../components/ui/orbiting-circles";
import { OpenAIIcon, N8NIcon, GoogleDriveIcon, ClaudeIcon, GmailIcon, WhatsAppIcon } from "../components/ui/icons";
import heroBackground from "../assets/Background for hero.mp4";
import automationBackground from "../assets/Background.mp4";

const projectData = [
  {
    id: 1,
    category: "Websites",
    tag: "Website",
    title: "GS Executive Transfers",
    client: "GS Executive Transfers — Chauffeur & Airport Transfer Service, Swindon",
    brief: "A premium chauffeur company needed a high-end, professional website to reflect their luxury service and drive online bookings.",
    built: "A multi-page website featuring vehicle fleet showcase, service pages, real-time booking flow, testimonials, and blog — built in HTML/CSS/JS with a sleek dark luxury aesthetic.",
    result: "A fully live, conversion-focused website now ranking locally in Swindon.",
    techTags: ["HTML", "CSS", "JavaScript"],
    link: "https://gsexecutivetransfers.co.uk",
    media: feature1,
    isAutomation: false
  },
  {
    id: 2,
    category: "Websites",
    tag: "Website",
    title: "All Areas Access",
    client: "All Areas Access — Rope Access Specialists",
    brief: "A rope access and specialist services company needed a clean, credible website to win commercial contracts.",
    built: "A professional multi-page website showcasing their services, safety credentials, and project work — built to establish trust with B2B clients.",
    result: "A polished online presence that positions them as a credible, professional contractor.",
    techTags: ["React", "CSS"],
    link: "https://abselling-site.vercel.app",
    media: feature2,
    isAutomation: false
  },
  {
    id: 3,
    category: "CRM & Tools",
    tag: "CRM & Tools",
    title: "Custom CRM for Education Company",
    client: "Education Company (name withheld)",
    brief: "An education business was managing student enquiries, enrolments, and follow-ups manually across spreadsheets and email — losing leads and wasting hours.",
    built: "A fully custom CRM tailored to their workflow — student pipeline management, automated follow-up tracking, enrolment status dashboard, and staff task management, all in one tool.",
    result: "Streamlined operations, zero missed follow-ups, and a significant reduction in admin time for the team.",
    techTags: ["React", "Node.js", "Database"],
    link: null,
    media: feature3,
    isAutomation: false
  },
  {
    id: 4,
    category: "Automations",
    tag: "Make.com Automation",
    title: "We build custom automations for your business",
    body: "Every business has repetitive tasks eating into their time. We design and build custom Make.com automation workflows that connect your apps, eliminate manual work, and keep your business running — even when you're not. Whether it's lead follow-ups, email sequences, data syncing, social posting, or order processing — we'll automate it for you.",
    cta: "Talk to us about automation →",
    media: null,
    isAutomation: true
  }
];

const filters = ["All Projects", "Websites", "CRM & Tools", "Automations"];

function ProjectVideo({ src }) {
  const videoRef = useRef(null);
  const isInView = useInView(videoRef, { margin: "200px 0px" });

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (isInView) {
      // Play when in view
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {
          // Autoplay was prevented
          const tryPlay = () => {
            video.play().catch(() => {});
            document.removeEventListener("click", tryPlay);
            document.removeEventListener("scroll", tryPlay);
          };
          document.addEventListener("click", tryPlay, { once: true });
          document.addEventListener("scroll", tryPlay, { once: true });
        });
      }
    } else {
      // Pause when out of view
      video.pause();
    }
  }, [isInView]);

  return (
    <video
      ref={videoRef}
      src={src}
      className="w-full h-full object-cover pointer-events-none block min-h-[300px]"
      autoPlay
      loop
      muted
      playsInline
      preload="auto"
    />
  );
}

export default function Projects() {
  const [activeFilter, setActiveFilter] = useState("All Projects");

  const filteredProjects = projectData.filter(p => 
    activeFilter === "All Projects" || p.category === activeFilter
  );

  return (
    <div className="pt-32 min-h-screen bg-black text-white flex flex-col justify-between relative">
      {/* Hero Background Video */}
      <div className="absolute top-0 left-0 w-full h-[60vh] md:h-[70vh] z-0 pointer-events-none overflow-hidden">
        <video
          src={heroBackground}
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/50 to-black pointer-events-none" />
      </div>

      <div className="flex-grow flex flex-col items-center px-4 sm:px-8 lg:px-16 w-full relative z-10">
        {/* Hero Section */}
        <div className="text-center mb-16 max-w-4xl mx-auto mt-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="liquid-glass rounded-full px-4 py-1.5 inline-block mb-6"
          >
            <span className="text-sm font-medium text-white font-body tracking-wide">
              Portfolio
            </span>
          </motion.div>
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-5xl md:text-6xl lg:text-7xl font-heading italic tracking-tight leading-[0.9] text-white"
          >
            Work we're proud of
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-8 text-white/60 font-body font-light text-lg md:text-xl max-w-3xl mx-auto leading-relaxed"
          >
            Every project starts with one question. <span className="text-white">What does this business actually need to grow?</span> Here's a selection of real work we've delivered.
          </motion.p>
        </div>

        {/* Filter Tabs */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="flex flex-wrap justify-center gap-3 mb-20"
        >
          {filters.map((filter) => (
            <button
              key={filter}
              onClick={() => setActiveFilter(filter)}
              className={`px-5 py-2 rounded-full text-sm font-medium font-body transition-all duration-300 ${
                activeFilter === filter 
                  ? "bg-white text-black shadow-[0_0_15px_rgba(255,255,255,0.3)]" 
                  : "liquid-glass text-white hover:bg-white/10"
              }`}
            >
              {filter}
            </button>
          ))}
        </motion.div>

        {/* Project Cards List */}
        <div className="w-full max-w-7xl mx-auto flex flex-col gap-12 lg:gap-20 pb-24">
          <AnimatePresence mode="popLayout">
            {filteredProjects.map((project, i) => (
              <motion.div
                layout
                initial={{ opacity: 0, scale: 0.95, y: 40 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: -40 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                key={project.id}
                className={`liquid-glass rounded-[2rem] p-8 md:p-12 lg:p-14 flex flex-col lg:flex-row items-center gap-12 lg:gap-20 border border-[#ffffff10] relative overflow-hidden ${
                  i % 2 !== 0 && !project.isAutomation ? "lg:flex-row-reverse" : ""
                }`}
              >
                {/* Automation Background Video */}
                {project.isAutomation && (
                  <video
                    src={automationBackground}
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="absolute inset-0 w-full h-full object-cover opacity-20 z-0 pointer-events-none"
                  />
                )}

                {/* Text Side */}
                <div className={`flex-1 w-full text-left relative z-10`}>
                  <div className={`text-xs font-semibold tracking-wider text-white/50 uppercase mb-4`}>
                    {project.tag}
                  </div>
                  <h3 className={`text-3xl md:text-4xl lg:text-5xl font-heading italic text-white tracking-tight leading-tight mb-8`}>
                    {project.title}
                  </h3>
                  
                  {project.isAutomation ? (
                    <div className="flex flex-col items-start text-left">
                      <p className="text-white/70 font-body font-light text-base md:text-lg leading-relaxed mb-10 max-w-lg">
                        {project.body}
                      </p>
                      <LiquidMetalButton
                        label={project.cta}
                        onClick={() => window.location.href = '/contact'}
                      />
                    </div>
                  ) : (
                    <div className="space-y-6 text-sm md:text-base font-body font-light">
                      <div>
                        <strong className="text-white font-medium block mb-1">Client</strong>
                        <span className="text-white/60">{project.client}</span>
                      </div>
                      <div>
                        <strong className="text-white font-medium block mb-1">Brief</strong>
                        <span className="text-white/60">{project.brief}</span>
                      </div>
                      <div>
                        <strong className="text-white font-medium block mb-1">What we built</strong>
                        <span className="text-white/60">{project.built}</span>
                      </div>
                      <div>
                        <strong className="text-white font-medium block mb-1">Result</strong>
                        <span className="text-white/60">{project.result}</span>
                      </div>
                      
                      <div className="flex flex-wrap gap-2 pt-4">
                        {project.techTags.map(t => (
                          <span key={t} className="px-4 py-1.5 bg-white/5 border border-white/10 rounded-full text-xs font-medium text-white/80">
                            {t}
                          </span>
                        ))}
                      </div>
                      
                      {project.link && (
                        <div className="pt-6">
                          <a 
                            href={project.link} 
                            target="_blank" 
                            rel="noreferrer" 
                            className="inline-flex items-center gap-2 text-white hover:text-white/70 transition-colors font-medium text-sm group"
                          >
                            View Live Project 
                            <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Media Side */}
                {project.isAutomation ? (
                  <div className="flex-1 w-full relative min-h-[350px] md:min-h-[450px] flex items-center justify-center lg:translate-x-8 overflow-visible">
                    <OrbitingCircles radius={80} duration={20}>
                      <N8NIcon className="w-7 h-7" />
                      <GmailIcon className="w-7 h-7" />
                    </OrbitingCircles>
                    <OrbitingCircles radius={140} duration={25} reverse>
                      <GoogleDriveIcon className="w-8 h-8" />
                      <WhatsAppIcon className="w-8 h-8" />
                    </OrbitingCircles>
                    <OrbitingCircles radius={200} duration={30}>
                      <OpenAIIcon className="w-9 h-9" />
                      <ClaudeIcon className="w-9 h-9" />
                    </OrbitingCircles>
                  </div>
                ) : project.media ? (
                  <Backlight className="flex-1 w-full" blur={40}>
                    <div className="w-full h-full rounded-2xl overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.5)] border border-[#ffffff15] bg-[#111]">
                      <ProjectVideo src={project.media} />
                    </div>
                  </Backlight>
                ) : null}
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>
      
      {/* Premium CTA */}
      <section className="relative w-full py-20 px-4 sm:px-8 lg:px-16 mt-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: false }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="relative max-w-5xl mx-auto"
        >
          {/* Animated gradient border wrapper */}
          <div className="relative rounded-2xl p-[1px] overflow-hidden">
            {/* Spinning gradient border */}
            <div 
              className="absolute inset-0 rounded-2xl"
              style={{
                background: "conic-gradient(from 0deg, transparent, rgba(255,255,255,0.15), transparent, rgba(255,255,255,0.08), transparent)",
                animation: "spin 8s linear infinite",
              }}
            />
            
            {/* Inner card */}
            <div className="relative liquid-glass rounded-2xl px-8 py-10 md:px-14 md:py-12 flex flex-col md:flex-row items-center justify-between gap-8 overflow-hidden">
              {/* Floating glow accents */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/[0.02] rounded-full blur-3xl pointer-events-none translate-x-1/3 -translate-y-1/3" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/[0.015] rounded-full blur-3xl pointer-events-none -translate-x-1/3 translate-y-1/3" />

              {/* Text */}
              <div className="relative z-10 text-center md:text-left flex-1">
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading italic text-white tracking-tight leading-[0.9] mb-3">
                  Got a project in mind?
                </h2>
                <p className="text-white/50 font-body font-light text-sm md:text-base max-w-lg leading-relaxed">
                  Let's talk about what we can build for you — websites, tools, or automations.
                </p>
              </div>

              {/* CTA Button */}
              <div className="relative z-10 flex-shrink-0">
                <LiquidMetalButton
                  label="Start a project →"
                  onClick={() => window.location.href = '/contact'}
                />
              </div>
            </div>
          </div>
        </motion.div>
      </section>
    </div>
  );
}
